/**
 * Backtest Data Transformer
 * 
 * Transforms backend API response to frontend component format.
 * Handles field name mapping and data structure normalization.
 */

/**
 * Transform a single backtest result from API format to frontend format
 * @param {Object} apiData - Raw API response data
 * @returns {Object} Transformed data for frontend components
 */
export const transformBacktestResult = (apiData) => {
  if (!apiData) return null;

  return {
    // Basic info - map field names (include both id formats for compatibility)
    id: apiData.id || apiData.backtestId,  // For series data fetch
    backtestId: apiData.id || apiData.backtestId,
    strategyName: apiData.strategyName || 'Unnamed Strategy',
    symbol: apiData.tickerName,
    timeframe: apiData.interval || '1h',
    startDate: apiData.startDate,
    endDate: apiData.endDate,
    tradingType: apiData.tradingType || 'Unknown',
    status: apiData.status,

    // Transform equity series
    equitySeries: transformEquitySeries(apiData.equitySeries),

    // Transform returns series
    returnsSeries: transformReturnsSeries(apiData.returnsSeries),

    // Transform trades
    trades: transformTrades(apiData.trades),

    // Transform price series
    priceSeries: transformPriceSeries(apiData.priceSeries),

    // Transform exposure series
    exposureSeries: transformExposureSeries(apiData.exposureSeries),

    trendLines: transformTrendLines(apiData.trendLines),

    // Transform summary
    summary: transformSummary(apiData.summary || apiData),

    // Charges summary (top-level)
    chargesSummary: transformChargesSummary(apiData.chargesSummary),
  };
};

/**
 * Transform equity series from API format
 */
const transformEquitySeries = (equitySeries) => {
  if (!equitySeries || !Array.isArray(equitySeries)) return [];

  return equitySeries.map(point => ({
    timestamp: point.timestamp,
    equity: point.equity,
  }));
};

const transformTrendLines = (trendLines) => {
  if (!trendLines || !Array.isArray(trendLines)) return [];

  return trendLines.map((line, index) => ({
    id: line.id ?? line.trendLineId ?? line.trend_line_id ?? `trendline-${index + 1}`,
    name: line.name ?? line.trendLineName ?? line.trend_line_name,
    x1: line.x1 ?? line.startTime ?? line.start_time,
    y1: line.y1 ?? line.startPrice ?? line.start_price,
    x2: line.x2 ?? line.endTime ?? line.end_time,
    y2: line.y2 ?? line.endPrice ?? line.end_price,
  }));
};

/**
 * Transform returns series from API format
 */
const transformReturnsSeries = (returnsSeries) => {
  if (!returnsSeries || !Array.isArray(returnsSeries)) return [];

  return returnsSeries.map(point => ({
    timestamp: point.timestamp,
    return: point.returnPct ?? point.returnValue ?? point.return ?? 0,
  }));
};

/**
 * Transform trades from API format
 */
const transformTrades = (trades) => {
  if (!trades || !Array.isArray(trades)) return [];

  return trades.map((trade, index) => ({
    trade_id: trade.tradeId || trade.trade_id || index + 1,
    entry_time: trade.entryTime || trade.entry_time,
    exit_time: trade.exitTime || trade.exit_time,
    entry_price: trade.entryPrice || trade.entry_price,
    exit_price: trade.exitPrice || trade.exit_price,
    qty: trade.quantity || trade.qty || 1,
    side: trade.side || trade.type || 'BUY',
    pnl: trade.pnl ?? trade.profitLoss ?? 0,
    return: trade.returnPct ?? trade.return ?? 0,
    // Charges & net P&L
    charges: trade.charges || null,
    net_pnl: trade.netPnl ?? trade.net_pnl ?? null,
  }));
};

/**
 * Transform charges summary from API format
 */
const transformChargesSummary = (cs) => {
  if (!cs) return null;
  return {
    totalBrokerage: cs.totalBrokerage ?? 0,
    totalStt: cs.totalStt ?? 0,
    totalGst: cs.totalGst ?? 0,
    totalStampDuty: cs.totalStampDuty ?? 0,
    totalTransactionCharges: cs.totalTransactionCharges ?? 0,
    totalSebiTurnoverFee: cs.totalSebiTurnoverFee ?? 0,
    totalDpCharges: cs.totalDpCharges ?? 0,
    totalCharges: cs.totalCharges ?? 0,
    netProfitLoss: cs.netProfitLoss ?? 0,
  };
};

/**
 * Transform price series (OHLCV) from API format
 */
const transformPriceSeries = (priceSeries) => {
  if (!priceSeries || !Array.isArray(priceSeries)) return [];

  return priceSeries.map(candle => ({
    timestamp: candle.timestamp,
    open: candle.open ?? candle.openPrice,
    high: candle.high ?? candle.highPrice,
    low: candle.low ?? candle.lowPrice,
    close: candle.close ?? candle.closePrice,
    volume: candle.volume ?? 0,
  }));
};

/**
 * Transform exposure series from API format
 */
const transformExposureSeries = (exposureSeries) => {
  if (!exposureSeries || !Array.isArray(exposureSeries)) return [];

  return exposureSeries.map(point => ({
    timestamp: point.timestamp,
    exposure_pct: point.exposurePct ?? point.exposure_pct ?? 0,
  }));
};

/**
 * Transform summary metrics from API format
 */
const transformSummary = (summary) => {
  if (!summary) return getDefaultSummary();

  return {
    initialCapital: summary.initialCapital ?? 100000,
    finalCapital: summary.finalCapital ?? summary.initialCapital ?? 100000,
    totalReturn: summary.totalReturn ?? 0,
    netProfit: summary.profitLoss ?? summary.netProfit ?? 0,
    grossProfit: summary.grossProfit ?? 0,
    grossLoss: summary.grossLoss ?? 0,
    totalTrades: summary.totalTrades ?? 0,
    winningTrades: summary.winningTrades ?? 0,
    losingTrades: summary.losingTrades ?? 0,
    winRate: summary.winRate ?? 0,
    profitFactor: summary.profitFactor ?? 0,
    maxDrawdown: summary.maxDrawdown ?? 0,
    sharpeRatio: summary.sharpeRatio ?? 0,
    avgTradeDuration: summary.avgTradeDuration ?? 'N/A',
    averageWin: summary.averageWin ?? 0,
    averageLoss: summary.averageLoss ?? 0,
  };
};

/**
 * Get default summary for empty/error states
 */
const getDefaultSummary = () => ({
  initialCapital: 100000,
  finalCapital: 100000,
  totalReturn: 0,
  netProfit: 0,
  grossProfit: 0,
  grossLoss: 0,
  totalTrades: 0,
  winningTrades: 0,
  losingTrades: 0,
  winRate: 0,
  profitFactor: 0,
  maxDrawdown: 0,
  sharpeRatio: 0,
  avgTradeDuration: 'N/A',
  averageWin: 0,
  averageLoss: 0,
});

/**
 * Transform a list of backtest results (for history view)
 * Returns simplified data without full series data
 */
export const transformBacktestList = (apiDataList) => {
  if (!apiDataList || !Array.isArray(apiDataList)) return [];

  return apiDataList.map(item => ({
    id: item.id || item.backtestId,
    strategyId: item.strategyId,
    strategyName: item.strategyName || 'Unnamed Strategy',
    symbol: item.tickerName,
    timeframe: item.interval || '1h',
    startDate: item.startDate,
    endDate: item.endDate,
    tradingType: item.tradingType || 'Unknown',
    executedAt: item.executedAt,
    status: item.status,
    totalReturn: item.totalReturn ?? 0,
    totalTrades: item.totalTrades ?? 0,
    winRate: item.winRate ?? 0,
    profitLoss: item.profitLoss ?? 0,
    maxDrawdown: item.maxDrawdown ?? 0,
    sharpeRatio: item.sharpeRatio ?? 0,
  }));
};

/**
 * Format date for display
 */
export const formatBacktestDate = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

/**
 * Format currency for display (INR)
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return '₹0';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format percentage for display
 */
export const formatPercent = (value, showSign = true) => {
  if (value === null || value === undefined) return '0%';

  const sign = showSign && value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

/**
 * Transform series data fetched separately from main backtest result
 * @param {Object} seriesData - Raw series data from API
 * @returns {Object} Transformed series data
 */
export const transformSeriesData = (seriesData) => {
  if (!seriesData) return null;

  return {
    equitySeries: transformEquitySeries(seriesData.equitySeries),
    returnsSeries: transformReturnsSeries(seriesData.returnsSeries),
    priceSeries: transformPriceSeries(seriesData.priceSeries),
    exposureSeries: transformExposureSeries(seriesData.exposureSeries),
  };
};

export default {
  transformBacktestResult,
  transformBacktestList,
  transformSeriesData,
  formatBacktestDate,
  formatCurrency,
  formatPercent,
};
